import { commonUtils } from '@/helpers/commonUtils'
import { Popover, PopoverProps } from 'antd'
import classNames from 'classnames'
import { CSSProperties, useEffect, useRef, useState } from 'react'
import style from './SearchPopover.module.less'

type MenuItem<T> = {
  label: string
  value: T
}
export interface SearchPopoverProps<T> extends PopoverProps {
  menuList: MenuItem<T>[]
  value: T
  placeholder?: string
  emptyPlaceholder?: string
  className?: string
  customStyles?: CSSProperties
  searchEnable?: boolean
  footerComponent?: React.ReactNode
  onValueChange: (value: T) => void
  onLoadChildren: (menuItem: MenuItem<T>) => React.ReactNode
  onRenderItem: (menuItem: MenuItem<T>) => React.ReactNode
}

export const SearchPopover = <T,>(props: SearchPopoverProps<T>) => {
  const {
    placeholder = '',
    emptyPlaceholder = '',
    menuList,
    value,
    className,
    customStyles,
    searchEnable = true,
    footerComponent,
    onValueChange,
    onLoadChildren,
    onRenderItem,
  } = props

  const [currentValueString, setCurrentValueString] = useState(value)
  const [open, setOpen] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [menuListInner, setMenuListInner] = useState<MenuItem<T>[]>([])
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus()
      })
    }
  }, [open])

  useEffect(() => {
    setCurrentValueString(value)
  }, [value])

  useEffect(() => {
    setMenuListInner(commonUtils.jsonClone(menuList))
  }, [JSON.stringify(menuList)])

  useEffect(() => {
    const keywordStr = keyword.toLowerCase()
    const searchResult = menuList.filter((item) => {
      if (item.label) {
        return !keywordStr || item.label.toLowerCase().indexOf(keywordStr) > -1
      }
      return !keywordStr || item.label.toLowerCase().indexOf(keywordStr) > -1
    })
    setMenuListInner(searchResult)
  }, [keyword, menuList])

  const currentLanguageItem = menuList.find((item) => item.value === currentValueString)

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    setKeyword('')
  }

  const content = (
    <div className={classNames(style.searchPopover, className)} style={{ ...customStyles }}>
      {searchEnable && (
        <div className={classNames(style.searchBox)}>
          <input
            ref={inputRef}
            className={classNames(style.input)}
            value={keyword}
            placeholder={placeholder}
            type='text'
            onChange={(e) => {
              setKeyword(e.target.value)
            }}
          />

          <div className={classNames(style.iconBox)}>
            {!!keyword && (
              <img
                className={classNames(style.closeIcon)}
                src={commonUtils.getStaticFile('option/icon__close-with-border.png')}
                onClick={() => {
                  setKeyword('')
                }}
              />
            )}
            {!keyword && (
              <img
                className={classNames(style.searchIcon)}
                src={commonUtils.getStaticFile('option/icon__search.png')}
              />
            )}
          </div>
        </div>
      )}
      <div
        className={classNames(style.contentList)}
        style={{ marginTop: searchEnable ? '9px' : '0' }}
      >
        {menuListInner.map((item, index) => {
          return (
            <div
              className={classNames(style.contentItem, {
                [style.active]: currentValueString === item.value,
              })}
              key={index}
              onClick={() => {
                onValueChange(item.value)
                setKeyword('')
                setOpen(false)
              }}
            >
              {/* <p className={classNames(style.main)}>{item.label}</p> */}
              {onRenderItem(item)}
            </div>
          )
        })}

        {menuListInner.length === 0 && (
          <div className={classNames(style.emptyBox)}>
            <span className={classNames(style.emptyText)}>{emptyPlaceholder}</span>
          </div>
        )}
      </div>
      {footerComponent}
    </div>
  )

  return (
    <Popover
      arrow={false}
      content={content}
      trigger={'click'}
      open={open}
      onOpenChange={handleOpenChange}
      overlayInnerStyle={{
        padding: 0,
        background: '#FFFFFF',
        boxShadow: '0px 2px 16px 0px rgba(93,93,93,0.15)',
        borderRadius: '10px',
      }}
      {...props}
    >
      {/* {children} */}
      {currentLanguageItem && onLoadChildren(currentLanguageItem)}
    </Popover>
  )
}
